import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import defaultLetterPartyTheme from '@/features/letterParty/themes/beach';
import { defaultThemeKey, LETTER_PARTY_THEME_KEYS, type LetterPartyTheme } from '@/features/letterParty/types';

import type { ReactNode } from 'react';

export { defaultThemeKey, LETTER_PARTY_THEME_KEYS } from '@/features/letterParty/types';
export type { LetterPartyTheme } from '@/features/letterParty/types';

const themeLoaders: Record<LETTER_PARTY_THEME_KEYS, () => Promise<{ default: LetterPartyTheme }>> = {
	[LETTER_PARTY_THEME_KEYS.BEACH]: () => Promise.resolve({ default: defaultLetterPartyTheme }),
};

const themeCache = new Map<LETTER_PARTY_THEME_KEYS, LetterPartyTheme>([[defaultThemeKey, defaultLetterPartyTheme]]);

const LetterPartyThemeContext = createContext<LetterPartyTheme | null>(null);

type LetterPartyThemeState = {
	resolvedKey: LETTER_PARTY_THEME_KEYS | null;
	theme: LetterPartyTheme | null;
	error: Error | null;
};

export const normalizeLetterPartyThemeKey = (key?: string | null): LETTER_PARTY_THEME_KEYS => {
	if (!key) {
		return defaultThemeKey;
	}

	const normalized = key.trim().toLowerCase();

	switch (normalized) {
		case LETTER_PARTY_THEME_KEYS.BEACH:
			return LETTER_PARTY_THEME_KEYS.BEACH;
		default:
			return defaultThemeKey;
	}
};

const getInitialThemeState = (key: LETTER_PARTY_THEME_KEYS): LetterPartyThemeState => {
	const cachedTheme = themeCache.get(key) ?? null;

	return {
		resolvedKey: cachedTheme ? key : null,
		theme: cachedTheme,
		error: null,
	};
};

const loadLetterPartyTheme = async (key: LETTER_PARTY_THEME_KEYS): Promise<LetterPartyTheme> => {
	const cachedTheme = themeCache.get(key);

	if (cachedTheme) {
		return cachedTheme;
	}

	const themeModule = await themeLoaders[key]();
	themeCache.set(key, themeModule.default);

	return themeModule.default;
};

export const getLetterPartyTheme = async (key?: string | null): Promise<LetterPartyTheme> => {
	const normalizedKey = normalizeLetterPartyThemeKey(key);

	try {
		return await loadLetterPartyTheme(normalizedKey);
	} catch (error) {
		console.error(
			`[LetterPartyTheme] Failed to load "${normalizedKey}" theme. Falling back to "${defaultThemeKey}".`,
			error,
		);
		return defaultLetterPartyTheme;
	}
};

export const useLetterPartyTheme = (): LetterPartyTheme => {
	const theme = useContext(LetterPartyThemeContext);

	if (!theme) {
		throw new Error('useLetterPartyTheme must be used within LetterPartyThemeProvider');
	}

	return theme;
};

type LetterPartyThemeProviderProps = {
	themeKey?: string | null;
	children: ReactNode;
};

export const LetterPartyThemeProvider = ({ themeKey, children }: LetterPartyThemeProviderProps) => {
	const normalizedThemeKey = useMemo(() => normalizeLetterPartyThemeKey(themeKey), [themeKey]);
	const [themeState, setThemeState] = useState<LetterPartyThemeState>(() => getInitialThemeState(normalizedThemeKey));

	useEffect(() => {
		let isActive = true;
		setThemeState(getInitialThemeState(normalizedThemeKey));

		getLetterPartyTheme(normalizedThemeKey)
			.then((loadedTheme) => {
				if (isActive) {
					setThemeState({
						resolvedKey: normalizedThemeKey,
						theme: loadedTheme,
						error: null,
					});
				}
			})
			.catch((error: unknown) => {
				if (isActive) {
					setThemeState({
						resolvedKey: null,
						theme: null,
						error: error instanceof Error ? error : new Error(String(error)),
					});
				}
			});

		return () => {
			isActive = false;
		};
	}, [normalizedThemeKey]);

	if (themeState.error) {
		throw themeState.error;
	}

	if (!themeState.theme || themeState.resolvedKey !== normalizedThemeKey) {
		return null;
	}

	return <LetterPartyThemeContext.Provider value={themeState.theme}>{children}</LetterPartyThemeContext.Provider>;
};
